import { CALENDAR_SHARING_LABELS, type CalendarSharing, type CalendarSourceItem, type MyCalendars } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { del, errorMessage, get, patch, post, put } from "../api";
import { Alert, Button, Field, Loading, Panel, PanelHeader, SelectField, formatDateTime } from "./ui";

const SHARING_HINT: Record<CalendarSharing, string> = { private: "Only you see these events.", busy: "The team sees a Busy block at those times, nothing else.", details: "The team sees each event's title, time, and place." };
const connectUrl = `/api/auth/google/calendar?returnTo=${encodeURIComponent("/profile")}`;

/** My profile → Calendars: Google (where appointments go, which calendars to show), iCal links, and my private iCal link. */
export function MyCalendarsCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["my-calendars"], queryFn: () => get<MyCalendars>("/api/me/calendars") });
  const set = (d: MyCalendars) => {
    qc.setQueryData(["my-calendars"], d);
    void qc.invalidateQueries({ queryKey: ["external-events"] });
  };
  const target = useMutation({ mutationFn: (body: { calendarId: string } | { create: true }) => put<{ moved: number; calendars: MyCalendars }>("/api/me/calendars/target", body), onSuccess: (r) => set(r.calendars) });
  const disconnect = useMutation({ mutationFn: () => del("/api/me/google-calendar"), onSuccess: () => qc.invalidateQueries({ queryKey: ["my-calendars"] }) });
  const sync = useMutation({ mutationFn: () => post<{ synced: number }>("/api/me/google-calendar/sync") });
  const show = useMutation({ mutationFn: (c: { id: string; name: string; color: string }) => post<MyCalendars>("/api/me/calendars/sources", { kind: "google", googleCalendarId: c.id, name: c.name, color: c.color, sharing: "private" }), onSuccess: set });
  const update = useMutation({ mutationFn: ({ id, ...body }: { id: string; sharing?: CalendarSharing; color?: string; name?: string }) => patch<MyCalendars>(`/api/me/calendars/sources/${id}`, body), onSuccess: set });
  const remove = useMutation({ mutationFn: (id: string) => del<MyCalendars>(`/api/me/calendars/sources/${id}`), onSuccess: set });
  const feed = useMutation({ mutationFn: (on: boolean) => (on ? post<MyCalendars>("/api/me/calendars/feed") : del<MyCalendars>("/api/me/calendars/feed")), onSuccess: set });
  const [copied, setCopied] = useState(false);
  if (q.isLoading) return <Loading />;
  if (q.error) return <Alert>{errorMessage(q.error)}</Alert>;
  const m = q.data!;
  const g = m.google;
  const shown = new Map(m.sources.filter((s) => s.kind === "google").map((s) => [s.googleCalendarId, s]));
  const icals = m.sources.filter((s) => s.kind === "ical");
  const error = target.error ?? show.error ?? update.error ?? remove.error ?? feed.error ?? disconnect.error;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-lg font-semibold">Calendars</h2>
        <p className="max-w-prose text-sm text-muted">Where your CRM appointments go, and other calendars to show on your CRM calendar. Each calendar you show has its own setting for what the team sees.</p>
      </div>
      {error && <Alert>{errorMessage(error)}</Alert>}

      <Panel>
        <PanelHeader
          title="Google Calendar"
          description={!g.available ? "Google Calendar isn't set up on this server." : g.connected ? `Connected as ${g.email}.` : "Put your CRM appointments on your Google Calendar, and show your Google calendars here."}
          action={
            g.available &&
            (g.connected ? (
              <div className="flex flex-wrap gap-2">
                <Button className="px-3 py-1.5" busy={sync.isPending} onClick={() => sync.mutate()}>
                  {sync.data ? `Synced ${sync.data.synced}` : "Sync now"}
                </Button>
                <Button variant="quiet" className="px-3 py-1.5" busy={disconnect.isPending} onClick={() => window.confirm("Stop putting your appointments on Google Calendar and stop showing your Google calendars here? Events already there stay.") && disconnect.mutate()}>
                  Disconnect
                </Button>
              </div>
            ) : (
              <Button variant="primary" className="px-3 py-1.5" onClick={() => (window.location.href = connectUrl)}>
                Connect Google Calendar
              </Button>
            ))
          }
        />
        {g.connected && (
          <div className="flex flex-col gap-4 px-5 py-4 text-sm">
            {g.error && <Alert>{g.error}</Alert>}
            {g.needsReconnect ? (
              <Alert tone="info">
                To pick where your appointments go and show your Google calendars here, Google needs to allow a little more (seeing your calendar list, and making an "NMT CRM" calendar).{" "}
                <a href={connectUrl} className="font-semibold underline">
                  Reconnect
                </a>
              </Alert>
            ) : (
              <>
                <div className={g.target ? "" : "rounded-[var(--radius-control)] border border-brand/40 bg-brand/5 p-3"}>
                  <div className="font-semibold">Where should your CRM appointments go?</div>
                  {!g.target && <p className="mb-2 text-muted">Until you pick, they go on your main calendar.</p>}
                  <div className="mt-1 flex flex-wrap items-end gap-2">
                    <SelectField
                      label="Calendar"
                      value={g.target?.id ?? ""}
                      onChange={(e) => e.target.value && target.mutate({ calendarId: e.target.value })}
                      className="min-w-56"
                      disabled={target.isPending}
                    >
                      {!g.target && <option value="">Pick one…</option>}
                      {g.calendars
                        .filter((c) => c.canWrite)
                        .map((c) => (
                          <option key={c.id} value={c.primary ? "primary" : c.id}>
                            {c.name}
                            {c.primary ? " (main)" : ""}
                          </option>
                        ))}
                    </SelectField>
                    {!g.calendars.some((c) => c.name === "NMT CRM") && (
                      <Button variant={g.target ? "secondary" : "primary"} className="px-3 py-2" busy={target.isPending} onClick={() => target.mutate({ create: true })}>
                        Make an "NMT CRM" calendar
                      </Button>
                    )}
                  </div>
                  {target.data && <p className="mt-1 text-ok">Done.{target.data.moved ? ` ${target.data.moved} appointment${target.data.moved === 1 ? "" : "s"} moved over.` : ""}</p>}
                </div>

                <div>
                  <div className="mb-1 font-semibold">Show on my CRM calendar</div>
                  <ul className="divide-y divide-mist rounded-[var(--radius-control)] border border-line">
                    {g.calendars.map((c) => {
                      const s = shown.get(c.id);
                      return (
                        <li key={c.id} className="flex flex-wrap items-center gap-3 px-3 py-2">
                          <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2">
                            <input
                              type="checkbox"
                              className="size-4 accent-[var(--color-brand)]"
                              checked={!!s}
                              disabled={show.isPending || remove.isPending}
                              onChange={(e) => (e.target.checked ? show.mutate(c) : s && remove.mutate(s.id))}
                            />
                            <span className="size-3 shrink-0 rounded-full" style={{ background: s?.color ?? c.color }} />
                            <span className="truncate">{c.name}</span>
                          </label>
                          {s && <SourceControls s={s} onChange={(body) => update.mutate({ id: s.id, ...body })} />}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </>
            )}
          </div>
        )}
      </Panel>

      <Panel>
        <PanelHeader title="Other calendars (iCal links)" description="Cozi, a school or team schedule, anything with an iCal or webcal link. They're read every 15 minutes; links are kept encrypted." />
        <div className="flex flex-col gap-3 px-5 py-4 text-sm">
          {icals.length > 0 && (
            <ul className="divide-y divide-mist rounded-[var(--radius-control)] border border-line">
              {icals.map((s) => (
                <li key={s.id} className="flex flex-wrap items-center gap-3 px-3 py-2">
                  <span className="size-3 shrink-0 rounded-full" style={{ background: s.color }} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{s.name}</div>
                    <div className={s.error ? "text-danger" : "text-xs text-muted"}>{s.error ?? (s.fetchedAt ? `Read ${formatDateTime(s.fetchedAt)}` : "Not read yet")}</div>
                  </div>
                  <SourceControls s={s} onChange={(body) => update.mutate({ id: s.id, ...body })} />
                  <Button variant="quiet" className="px-2 py-1" onClick={() => window.confirm(`Remove ${s.name}?`) && remove.mutate(s.id)}>
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <AddIcal onAdded={set} />
        </div>
      </Panel>

      <Panel>
        <PanelHeader title="My private iCal link" description="Your CRM appointments as a link to subscribe to in Fantastical, Apple Calendar, or Outlook. It works like a password: anyone with it sees your appointments. Those apps check it on their own schedule (Fantastical and Apple every 5 to 15 minutes; Google only every several hours)." />
        <div className="flex flex-col gap-2 px-5 py-4 text-sm">
          {m.feedUrl ? (
            <>
              <input readOnly value={m.feedUrl} onFocus={(e) => e.target.select()} className="w-full rounded-[var(--radius-control)] border border-line bg-mist px-3 py-2" />
              <div className="flex flex-wrap gap-2">
                <Button className="px-3 py-1.5" onClick={() => navigator.clipboard.writeText(m.feedUrl!).then(() => setCopied(true))}>
                  {copied ? "Copied" : "Copy"}
                </Button>
                <Button variant="quiet" className="px-3 py-1.5" busy={feed.isPending} onClick={() => window.confirm("Make a new link? The old one stops working.") && feed.mutate(true)}>
                  Make a new link
                </Button>
                <Button variant="quiet" className="px-3 py-1.5" busy={feed.isPending} onClick={() => window.confirm("Turn off your iCal link? Calendars subscribed to it stop updating.") && feed.mutate(false)}>
                  Turn off
                </Button>
              </div>
            </>
          ) : (
            <div>
              <Button className="px-3 py-1.5" busy={feed.isPending} onClick={() => feed.mutate(true)}>
                Make my link
              </Button>
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
}

const COLORS = ["#64748B", "#2563EB", "#16A34A", "#9333EA", "#DB2777", "#EA580C", "#0891B2", "#CA8A04"];

/** Who sees a calendar, and its color. */
function SourceControls({ s, onChange }: { s: CalendarSourceItem; onChange: (body: { sharing?: CalendarSharing; color?: string }) => void }) {
  return (
    <div className="flex items-center gap-2">
      <input type="color" aria-label={`${s.name} color`} value={s.color} onChange={(e) => onChange({ color: e.target.value.toUpperCase() })} className="h-7 w-8 cursor-pointer rounded border border-line" />
      <select aria-label={`Who sees ${s.name}`} title={SHARING_HINT[s.sharing]} value={s.sharing} onChange={(e) => onChange({ sharing: e.target.value as CalendarSharing })} className="rounded-[var(--radius-control)] border border-line bg-white px-2 py-1 text-sm">
        {(Object.keys(CALENDAR_SHARING_LABELS) as CalendarSharing[]).map((k) => (
          <option key={k} value={k}>
            {CALENDAR_SHARING_LABELS[k]}
          </option>
        ))}
      </select>
    </div>
  );
}

function AddIcal({ onAdded }: { onAdded: (d: MyCalendars) => void }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ url: "", name: "", color: COLORS[1]!, sharing: "private" as CalendarSharing });
  const add = useMutation({
    mutationFn: () => post<MyCalendars>("/api/me/calendars/sources", { kind: "ical", ...form }),
    onSuccess: (d) => {
      onAdded(d);
      setOpen(false);
      setForm({ url: "", name: "", color: COLORS[(COLORS.indexOf(form.color) + 1) % COLORS.length]!, sharing: "private" });
    },
  });
  if (!open)
    return (
      <div>
        <Button className="px-3 py-1.5" onClick={() => setOpen(true)}>
          + Add an iCal link
        </Button>
      </div>
    );
  return (
    <form
      className="grid gap-3 rounded-[var(--radius-control)] border border-line p-3 sm:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        add.mutate();
      }}
    >
      <Field label="iCal or webcal link" required className="sm:col-span-2" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="webcal://… or https://….ics" hint="In Cozi: Settings → Calendar → Share & Subscribe (iCal feed). Most calendars have an iCal, ICS, or subscribe link." />
      <Field label="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Cozi" />
      <SelectField label="Who sees it" value={form.sharing} onChange={(e) => setForm({ ...form, sharing: e.target.value as CalendarSharing })} hint={SHARING_HINT[form.sharing]}>
        {(Object.keys(CALENDAR_SHARING_LABELS) as CalendarSharing[]).map((k) => (
          <option key={k} value={k}>
            {CALENDAR_SHARING_LABELS[k]}
          </option>
        ))}
      </SelectField>
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-ink-soft">Color</span>
        {COLORS.map((c) => (
          <button key={c} type="button" aria-label={c} onClick={() => setForm({ ...form, color: c })} className={`size-6 rounded-full ${form.color === c ? "ring-2 ring-ink ring-offset-2" : ""}`} style={{ background: c }} />
        ))}
      </div>
      {add.error && <div className="sm:col-span-2"><Alert>{errorMessage(add.error)}</Alert></div>}
      <div className="flex gap-2 sm:col-span-2">
        <Button type="submit" variant="primary" busy={add.isPending}>
          {add.isPending ? "Reading it…" : "Add"}
        </Button>
        <Button type="button" variant="quiet" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
